import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Panel } from "@/components/portal/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setInfo(false); return; }
      try {
        const { data } = await api.get(`/auth/invite-info?token=${encodeURIComponent(token)}`);
        setInfo(data);
      } catch {
        setInfo(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name: name.trim(), password });
      setUser(data);
      toast.success("Account created");
      navigate(data.role === "admin" ? "/" : (data.athlete_id ? `/athlete/${data.athlete_id}` : "/"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="w-full max-w-md">
        <div className="mb-8"><Logo size={40} /></div>
        <Panel className="p-8">
          {info === null ? (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Validating invitation…</div>
          ) : !info ? (
            <div data-testid="invite-invalid">
              <h2 className="font-display text-2xl text-white mb-2">Invitation not valid</h2>
              <p className="text-sm text-muted-foreground">This invitation is invalid or has expired. Ask a Merch Run admin for a fresh link.</p>
              <Link to="/login" className="inline-block text-sm text-white mt-6 hover:text-[#E1FF00]">Go to sign in</Link>
            </div>
          ) : (
            <>
              <p className="overline mb-2">You're invited · {info.role}</p>
              <h2 className="font-display text-2xl text-white mb-1">Set up your account</h2>
              <p className="text-sm text-muted-foreground mb-6">{info.email}</p>
              <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
                <div className="space-y-2">
                  <Label>Your name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name-input" className="bg-secondary border-border h-11" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" minLength={10} required placeholder="At least 10 characters" data-testid="invite-password-input" className="bg-secondary border-border h-11" />
                </div>
                {error && <div className="text-sm text-[#FF6666]" data-testid="invite-error">{error}</div>}
                <Button type="submit" disabled={loading} className="w-full h-11 bg-white text-black hover:bg-white/90 font-semibold" data-testid="invite-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </form>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
